import { useState } from "react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import {
  logout as apiLogout,
  setAuthToken,
} from "@/lib/api";
import { removeAuthenticatedFromCache } from "@/lib/queryClient";
import { revokeAllThumbnails } from "@/features/coach-review/hooks/useThumbnail";
import { useMe } from "@/features/auth/useMe";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import { Logo } from "@/components/brand/Logo";
import { getPublicNavCapabilities } from "./navCapabilities";
import {
  getInitialTheme,
  setTheme,
  type ThemePreference,
} from "./themePreference";
import { useBrandSync } from "./useBrandSync";

/** Shared app header rendered at the root layout so every route gets
 * the same nav + auth boundary + theme toggle.
 *
 * Product branding (mark + wordmark) is bundled with the app — see
 * `web/public/brand/`. The product name `app_name` ("tktic") is
 * fixed and is NOT operator-configurable — the inline wordmark
 * always says "tktic", and the backend rejects PUTs against the
 * `app_name` settings key (see `tests/test_settings.py::
 * test_app_name_is_not_writable`).
 *
 * Operator branding is a separate concern: `season_title` (the
 * club title) and the uploaded `favicon_url` flow into the browser
 * tab (title + favicon) via `useBrandSync` below. That's about the
 * operator's club identity on the tab strip, not the product name.
 *
 * Public pages stay anonymous-readable; signed-in users see the
 * role-gated Coach/My Feedback/Admin links.
 */
export function PublicAppHeader() {
  useMe();
  useBrandSync();
  const authMe = useAuthStore((s) => s.me);
  const location = useLocation();
  const navigate = useNavigate();
  const applyAnon = useAuthStore((s) => s.applyAnonymous);
  const clearScope = useScopeStore((s) => s.clear);
  const [theme, setThemeState] = useState<ThemePreference>(() => getInitialTheme());
  const [signOutPending, setSignOutPending] = useState(false);

  const caps = getPublicNavCapabilities(authMe);
  const loginNext =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}${window.location.hash}` || "/"
      : location.pathname || "/";

  const toggleTheme = () => {
    setThemeState((prev) => setTheme(prev === "dark" ? "light" : "dark"));
  };

  const signOut = async () => {
    if (signOutPending) return;
    setSignOutPending(true);
    try {
      try {
        await apiLogout();
      } catch {
        // Server-side session may already be gone; local cleanup is authoritative.
      }
      setAuthToken(null);
      applyAnon();
      clearScope();
      removeAuthenticatedFromCache();
      revokeAllThumbnails();
      void navigate({ to: "/", replace: true });
    } finally {
      setSignOutPending(false);
    }
  };

  return (
    <header
      data-testid="public-app-header"
      className="sticky top-0 z-50 border-b border-rule bg-surface/95 text-ink backdrop-blur"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/"
            data-testid="public-header-brand"
            aria-label="tktic — home"
            className="flex items-center text-ink hover:text-accent transition-colors"
          >
            <Logo
              size={28}
              data-testid="public-header-app-logo"
            />
          </Link>
          <nav
            aria-label="Public navigation"
            className="flex flex-wrap items-center gap-2 text-[0.68rem] font-display uppercase tracking-wider"
          >
            <Link
              to="/teams"
              data-testid="public-header-teams-link"
              className="rounded border border-rule px-2.5 py-1 text-ink-muted hover:border-accent hover:text-ink transition-colors"
            >
              Teams
            </Link>
            {caps.canOpenCoach ? (
              <Link
                to="/coach"
                data-testid="public-header-coach-link"
                className="rounded border border-rule px-2.5 py-1 text-ink-muted hover:border-accent hover:text-ink transition-colors"
              >
                Coach
              </Link>
            ) : null}
            {caps.canOpenFeedback ? (
              <Link
                to="/feedback"
                data-testid="public-header-feedback-link"
                className="rounded border border-rule px-2.5 py-1 text-ink-muted hover:border-accent hover:text-ink transition-colors"
              >
                My Feedback
              </Link>
            ) : null}
            {caps.canOpenAdmin ? (
              <Link
                to="/admin"
                data-testid="public-header-admin-link"
                className="rounded border border-rule px-2.5 py-1 text-ink-muted hover:border-accent hover:text-ink transition-colors"
              >
                Admin
              </Link>
            ) : null}
          </nav>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-[0.68rem] font-display uppercase tracking-wider">
          <button
            type="button"
            onClick={toggleTheme}
            data-testid="public-header-theme-toggle"
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
            className="rounded border border-rule px-2.5 py-1 text-ink-muted hover:border-accent hover:text-ink transition-colors"
          >
            {theme === "dark" ? "Light" : "Dark"} mode
          </button>

          {caps.signedIn ? (
            <>
              <Link
                to="/me"
                data-testid="public-header-account-link"
                className="rounded border border-rule px-2.5 py-1 text-ink hover:border-accent transition-colors"
              >
                {caps.displayName || "Account"}
              </Link>
              <button
                type="button"
                onClick={() => void signOut()}
                disabled={signOutPending}
                data-testid="public-header-logout"
                className="rounded border border-destructive/40 px-2.5 py-1 text-destructive hover:bg-destructive/10 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                {signOutPending ? "Signing out…" : "Logout"}
              </button>
            </>
          ) : (
            <Link
              to="/login"
              search={{ next: loginNext }}
              data-testid="public-header-login-link"
              className="rounded bg-accent px-3 py-1 text-white hover:bg-accent-hover transition-colors"
            >
              Login
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
